import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

export interface EvolutionStatus {
    isEvolutionAPI: boolean;
}

/** Corpo do POST /Criar. Sem `conexaoPlataformaId` cria uma conexão nova;
 *  com ele, recria a instância na mesma conexão (mesmas conversas). */
export interface CriarInstanciaEvolutionRequest {
    nome?: string;
    conexaoPlataformaId?: number;
}

/** Conexão devolvida pelo /Criar (status: 0 = Inativa até parear o QR). */
export interface ConexaoEvolution {
    conexaoPlataformaId: number;
    nome: string;
    plataforma: number;
    status: number;
}

export interface QrCodeEvolution {
    conexaoPlataformaId: number;
    conectado: boolean;
    qrCodeBase64: string | null;
}

export interface StatusInstanciaEvolution {
    conexaoPlataformaId: number;
    conectado: boolean;
    sessaoAtiva: boolean;
    nomePerfil: string | null;
    status: number;
}

export const EvolutionService = {
    /**
     * Verifica se a empresa do usuário logado já tem uma conexão Evolution ativa.
     */
    checkEvolution: async (): Promise<ApiResponse<EvolutionStatus>> => {
        const response = await apiFetch(`${API_URL}/api/InstanciaEvolution/EmpresaUsaEvolution`);
        return response.json();
    },

    /**
     * Cria uma conexão nova (Inativa) ou recria a instância numa conexão existente.
     * Em seguida, use `getQRCode` para parear o número.
     */
    criar: async (body: CriarInstanciaEvolutionRequest = {}): Promise<ApiResponse<ConexaoEvolution>> => {
        const response = await apiFetch(`${API_URL}/api/InstanciaEvolution/Criar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return response.json();
    },

    /**
     * Inicia a sessão (se preciso) e devolve o QR code. Chame a cada ~3s até
     * `dados.conectado === true`.
     */
    getQRCode: async (conexaoPlataformaId: number): Promise<ApiResponse<QrCodeEvolution>> => {
        const response = await apiFetch(`${API_URL}/api/InstanciaEvolution/QRCode/${conexaoPlataformaId}`);
        return response.json();
    },

    /**
     * Status da instância; promove a conexão para Ativa se o número estiver pareado.
     */
    getStatus: async (conexaoPlataformaId: number): Promise<ApiResponse<StatusInstanciaEvolution>> => {
        const response = await apiFetch(`${API_URL}/api/InstanciaEvolution/Status/${conexaoPlataformaId}`);
        return response.json();
    },

    /**
     * Desfaz o pareamento (a conexão continua existindo, vira Inativa).
     */
    logout: async (conexaoPlataformaId: number): Promise<ApiResponse<null>> => {
        const response = await apiFetch(`${API_URL}/api/InstanciaEvolution/Logout/${conexaoPlataformaId}`, {
            method: 'DELETE',
        });
        return response.json();
    },

    /**
     * Apaga a instância na Evolution Go e faz soft delete da conexão.
     */
    remover: async (conexaoPlataformaId: number): Promise<ApiResponse<null>> => {
        const response = await apiFetch(`${API_URL}/api/InstanciaEvolution/Remover/${conexaoPlataformaId}`, {
            method: 'DELETE',
        });
        return response.json();
    },
};
